import { Box, Button, DialogActions, DialogContent, Stack, TextField } from '@mui/material';
import { FilterWrapper } from '../shared/all.styles';
import { Typography } from '../shared/typography';
import { useTranslation } from 'react-i18next';
import { ClusterFilters, ICluster, IProducts } from '../models/interfaces';
import { SearchFnReturn, useBaseList } from '../hook/useBaseList';
import { convertStringsToSort, tranformSearchFiltersForBE } from '../utils/helpers';
import { AxiosResponse } from 'axios';
import { useApi } from '../context/useApi';
import { format } from 'date-fns';
import { List } from '../shared/list';
import { useState } from 'react';

const FILTER_STORE_KEY = 'quick_cluster_search';

interface Props {
  onClose: () => void;
  onAssociate: (clusterId: string) => void;
}

export const QuickSearchClusterDialog = ({ onClose, onAssociate }: Props) => {
  const { t } = useTranslation();
  const { defaultApi } = useApi();

  const [selectedId, setSelectedId] = useState<string[]>([]);

  const searchFn = async (
    page: number,
    size: number,
    filters: ClusterFilters,
    sort?: string[],
  ): Promise<SearchFnReturn<ICluster>> => {
    filters = tranformSearchFiltersForBE(filters);
    let resp: AxiosResponse<any>;
    filters = { ...filters };
    if (sort && sort.length > 0) {
      resp = await defaultApi.clustersSearchPost({
        params: { page: page, size: size, sort },
        data: { ...filters },
      });
    } else {
      resp = await defaultApi.clustersSearchPost({
        params: { page: page, size: size },
        data: { ...filters },
      });
    }

    if (resp.status === 200) {
      return { count: resp.data?.data.totalElements ?? 0, list: resp.data.data?.content ?? [] };
    } else {
      throw Error('Errore');
    }
  };

  const {
    list,
    page,
    pageSize,
    count,
    isLoading,
    isPreviousData,
    handlePageChange,
    handlePageSizeChange,
    clearFilters,
    sort,
    onSortClick,
    filtersForm: formProps,
  } = useBaseList<ICluster, ClusterFilters>({
    searchFn: searchFn,
    defaultFilters: {},
    filterStoreKey: FILTER_STORE_KEY,
    apiOptions: { cacheTime: 20000 },
  });

  return (
    <>
      <DialogContent>
        <div style={{ width: '80vw', height: '70vh' }}>
          <FilterWrapper grayBackground>
            <Stack
              direction={'row'}
              spacing={3}
              alignItems={'center'}
              justifyContent={'space-between'}
            >
              <Stack direction={'row'} spacing={3} minWidth={'50%'} alignItems={'center'}>
                <Typography variant="subtitle1">{t('cluster.filters')}</Typography>
                <Box
                  sx={{
                    width: '1px',
                    backgroundColor: 'gray',
                    height: '40px',
                    display: 'block',
                    opacity: 0.8,
                  }}
                />
                <TextField
                  {...formProps.bindTextField({ field: 'full_text' })}
                  label={t('cluster.descriptionContains')}
                  fullWidth
                  sx={{ borderRadius: '30px' }}
                />
              </Stack>

              <Stack direction={'row'} spacing={3} alignItems={'center'}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={clearFilters}
                  sx={{ mr: '30px', whiteSpace: 'nowrap' }}
                >
                  {t('actions.clearFilters')}
                </Button>
                <Button onClick={() => formProps.submit()} variant="contained" color="primary">
                  {t('actions.search')}
                </Button>
              </Stack>
            </Stack>
          </FilterWrapper>
          <Stack direction={'row'} spacing={3} alignItems={'center'}>
            <Typography>{t('info.otherClusterAssocInfo')}</Typography>
            <Button
              onClick={() => {
                if (!selectedId || selectedId?.length < 1) return;
                onAssociate(selectedId[0]);
                onClose()
              }}
              variant="contained"
              disabled={(selectedId?.length ?? 0) < 1}
            >
              {t('actions.associate')}
            </Button>
          </Stack>
          <Stack padding={2}>
            <List
              maxheight="calc(100vh - 20px)"
              list={list}
              count={count}
              page={page}
              pageSize={pageSize}
              loading={isLoading}
              isPreviousData={isPreviousData}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              gridTemplateColumns={'3fr 6fr 1fr '}
              sortBy={convertStringsToSort(sort)}
              onSortClick={onSortClick}
              onItemClick={(e: ICluster) => {
                if (selectedId?.includes(e.cluster_id)) {
                  setSelectedId([]);
                } else {
                  setSelectedId([e.cluster_id]);
                }
              }}
              selectedIds={selectedId}
              onSelectedIdsChange={(e) => {
                setSelectedId(e);
              }}
              keyGetter={(i: ICluster) => i.cluster_id}
              onlyOne
              fields={[
                {
                  title: t('cluster.id'),
                  key: 'cluster_id',
                  sortable: false,
                },
                {
                  title: t('cluster.description'),
                  key: 'descriptions',
                  sortable: false,
                  tooltip: true,
                },
                {
                  title: t('cluster.lastUpdate'),
                  valueGetter: (item: ICluster) => {
                    return format(new Date(item.updated_at), 'dd/MM/yyyy hh:mm');
                  },
                  key: 'last_update',
                  sortable: false,
                },
              ]}
              emptyListMessage={t('info.noResult')}
              keepRecentlyOpenedItem
              // lastOpenedItem={lastOpenedItem}
            />
          </Stack>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('actions.cancel')}</Button>
      </DialogActions>
    </>
  );
};
